import type { BoardData, Item, ItemKind } from '../model/schema.js';
import { worldPosition } from '../model/areas.js';
import { frameTitle } from '../model/board.js';
import { isListHolder } from '../materials/list.js';
import { normalizeToken } from '../catalog/catalog.js';
import { fieldColumns } from './columns.js';

/**
 * Change-over list between two acts on one stage (U47, OIL-21, R11 "cheap via comparison"):
 * what to strike from the previous act's board, what to set for the next one, what moves and
 * what stays. Items of the two boards are matched by what they are (kind + brand / model, else
 * label), nearest position first.
 */

export type ChangeoverAction = 'strike' | 'move' | 'set' | 'stay';
export const CHANGEOVER_ACTIONS: readonly ChangeoverAction[] = ['strike', 'move', 'set', 'stay'];

export interface ChangeoverRow {
  rowKey: string;
  action: ChangeoverAction;
  kind: ItemKind;
  name: string;
  /** Position on the previous board ("DSL 7.2 / 5.1 m"), empty for "set". */
  from: string;
  /** Position on the next board, empty for "strike". */
  to: string;
  /** Channels of the item on the board it belongs to (next, else previous). */
  channels: string;
}

/** Further than this (m) counts as a move. */
export const MOVE_THRESHOLD_M = 0.5;

const ZONES = [
  ['USR', 'USC', 'USL'],
  ['CSR', 'C', 'CSL'],
  ['DSR', 'DSC', 'DSL'],
] as const;

/**
 * "DSL 7.2 / 5.1 m" (German "DSL 7,2 / 5,1 m", OIL-200) – stage zone (seen from the performer)
 * and frame position; in another area "FOH 1.0 / 1.0 m" (area name, OIL-281); "" unplaced or free.
 */
export function positionText(data: BoardData, item: Item, locale: 'en' | 'de' = 'en'): string {
  const at = placedAt(data, item);
  if (at !== item) return `${positionText(data, at, locale)} · ${nameOf(at)}`;
  const frame = item.frameId === null ? undefined : data.frames[item.frameId];
  if (!frame) return '';
  const n = (v: number) => (locale === 'de' ? v.toFixed(1).replace('.', ',') : v.toFixed(1));
  if (frame.kind !== 'stage') {
    return `${frameTitle(frame.name, locale)} ${n(item.x)} / ${n(item.y)} m`;
  }
  const col = Math.min(2, Math.max(0, Math.floor((item.x / frame.width) * 3)));
  const row = Math.min(2, Math.max(0, Math.floor((item.y / frame.depth) * 3)));
  return `${ZONES[row]![col]!} ${n(item.x)} / ${n(item.y)} m`;
}

/**
 * Where an item is: itself, or – for an item in a group without a place of its own (a mic in a
 * drum kit's list, OIL-234) – the nearest placed group above it.
 */
function placedAt(data: BoardData, item: Item): Item {
  const seen = new Set<string>([item.id]);
  let at = item;
  while (
    worldPosition(data, at) === undefined &&
    at.parentId !== undefined &&
    !seen.has(at.parentId)
  ) {
    const parent = data.items[at.parentId];
    if (parent === undefined) return item;
    seen.add(parent.id);
    at = parent;
  }
  return worldPosition(data, at) === undefined ? item : at;
}

function nameOf(item: Item): string {
  const bm = [item.brand === 'Generic' ? undefined : item.brand, item.model]
    .filter((s) => s !== undefined && s.trim() !== '')
    .join(' ');
  return item.label.trim() || bm || item.kind;
}

/** What an item is, for matching across boards. */
function keyOf(item: Item): string {
  const bm = normalizeToken(`${item.brand ?? ''}${item.model ?? ''}`);
  return `${item.kind}:${bm !== '' ? bm : normalizeToken(item.label)}`;
}

function gear(data: BoardData): Item[] {
  return Object.values(data.items).filter((i) => i.kind !== 'musician' && !isListHolder(i));
}

function channelsOf(data: BoardData, id: string): string {
  return Object.values(data.channels)
    .filter((c) => c.sourceItemId === id)
    .map((c) => c.number)
    .sort((a, b) => a - b)
    .join(', ');
}

/** Distance between where two items are (their groups for items in a list, OIL-234), by world position. */
const placedDist = (prev: BoardData, a: Item, next: BoardData, b: Item) => {
  const pa = worldPosition(prev, placedAt(prev, a));
  const pb = worldPosition(next, placedAt(next, b));
  return pa === undefined || pb === undefined
    ? Number.POSITIVE_INFINITY
    : Math.hypot(pa.x - pb.x, pa.y - pb.y);
};

/** The change-over from `previous` to `next`: strike, move, set, stay – each by kind and name. */
export function changeoverList(
  previous: BoardData,
  next: BoardData,
  locale: 'en' | 'de' = 'en',
): ChangeoverRow[] {
  const free = new Map<string, Item[]>();
  for (const i of gear(previous)) free.set(keyOf(i), [...(free.get(keyOf(i)) ?? []), i]);
  const rows: ChangeoverRow[] = [];
  // nearest pairs first, so two identical wedges keep their sides
  const wanted = gear(next).map((to) => ({ to, key: keyOf(to) }));
  const pairs: { to: Item; from: Item; d: number }[] = [];
  for (const { to, key } of wanted)
    for (const from of free.get(key) ?? [])
      pairs.push({ to, from, d: placedDist(previous, from, next, to) });
  pairs.sort(
    (a, b) => a.d - b.d || a.to.id.localeCompare(b.to.id) || a.from.id.localeCompare(b.from.id),
  );
  const usedTo = new Set<string>();
  const usedFrom = new Set<string>();
  for (const p of pairs) {
    if (usedTo.has(p.to.id) || usedFrom.has(p.from.id)) continue;
    usedTo.add(p.to.id);
    usedFrom.add(p.from.id);
    const moved =
      !(p.d <= MOVE_THRESHOLD_M) &&
      !(
        worldPosition(previous, placedAt(previous, p.from)) === undefined &&
        worldPosition(next, placedAt(next, p.to)) === undefined
      );
    rows.push({
      rowKey: `pair:${p.from.id}:${p.to.id}`,
      action: moved ? 'move' : 'stay',
      kind: p.to.kind,
      name: nameOf(p.to),
      from: positionText(previous, p.from, locale),
      to: positionText(next, p.to, locale),
      channels: channelsOf(next, p.to.id),
    });
  }
  for (const to of gear(next))
    if (!usedTo.has(to.id))
      rows.push({
        rowKey: `set:${to.id}`,
        action: 'set',
        kind: to.kind,
        name: nameOf(to),
        from: '',
        to: positionText(next, to, locale),
        channels: channelsOf(next, to.id),
      });
  for (const from of gear(previous))
    if (!usedFrom.has(from.id))
      rows.push({
        rowKey: `strike:${from.id}`,
        action: 'strike',
        kind: from.kind,
        name: nameOf(from),
        from: positionText(previous, from, locale),
        to: '',
        channels: channelsOf(previous, from.id),
      });
  const order = (a: ChangeoverAction) => CHANGEOVER_ACTIONS.indexOf(a);
  return rows.sort(
    (a, b) =>
      order(a.action) - order(b.action) ||
      a.kind.localeCompare(b.kind) ||
      a.name.localeCompare(b.name) ||
      a.rowKey.localeCompare(b.rowKey),
  );
}

export const CHANGEOVER_COLUMNS = fieldColumns<ChangeoverRow>('changeover', [
  'action',
  'kind',
  'name',
  'from',
  'to',
  'channels',
]);
